import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Edit, Trash, RefreshCw } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import QuestionManagementMenu from "@/components/admin/QuestionManagementMenu";
import QuestionEditor from "@/components/admin/QuestionEditor";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DataTable } from "@/components/common/DataTable";
import { DataPagination } from "@/components/common/DataPagination";
import { ListToolbar } from "@/components/common/ListToolbar";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { queryKeys } from "@/lib/queryKeys";
import { useUrlState } from "@/lib/urlState";
import { exportRowsAsCsv } from "@/lib/csv";
import { useToast } from "@/hooks/use-toast";

interface QuestionRow {
  id: string;
  text: string;
  explanation?: string | null;
  subject?: { name: string; grade: string } | null;
  options: { id: string; text: string; is_correct: boolean }[];
}

const GRADE_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const AdminQuestions = () => {
  const url = useUrlState();
  const { toast } = useToast();
  const qc = useQueryClient();

  const selectedGrade = url.get("grade") ?? "";
  const selectedSubject = url.get("subject") ?? "";
  const selectedCategory = url.get("category") ?? "";
  const search = url.get("q") ?? "";

  const [selectedRows, setSelectedRows] = useState<QuestionRow[]>([]);
  const [editing, setEditing] = useState<QuestionRow | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { data: subjects = [] } = useQuery({
    queryKey: ["admin", "subjects-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subjects")
        .select("id, name, grade")
        .order("grade")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["admin", "categories-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subjects_categories")
        .select("id, name, subject_id")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const filteredSubjects = useMemo(
    () => (selectedGrade ? subjects.filter((s) => s.grade === selectedGrade) : subjects),
    [subjects, selectedGrade],
  );
  const filteredCategories = useMemo(
    () =>
      selectedSubject
        ? categories.filter((c) => c.subject_id === selectedSubject)
        : categories,
    [categories, selectedSubject],
  );

  const subjectIdsForGrade = useMemo(
    () => (selectedGrade ? subjects.filter((s) => s.grade === selectedGrade).map((s) => s.id) : null),
    [subjects, selectedGrade],
  );

  const paginated = usePaginatedQuery<QuestionRow>({
    queryKey: queryKeys.questions.list({
      page: 0,
      pageSize: 25,
      filters: { selectedGrade, selectedSubject, selectedCategory, search },
    }),
    table: "questions",
    select: "id, text, explanation, subject:subjects(name, grade), options(id, text, is_correct)",
    orderBy: { column: "created_at", ascending: false },
    syncToUrl: true,
    filters: (q) => {
      let next = q;
      if (selectedSubject) next = next.eq("subject_id", selectedSubject);
      else if (subjectIdsForGrade && subjectIdsForGrade.length > 0)
        next = next.in("subject_id", subjectIdsForGrade);
      if (selectedCategory) next = next.eq("category_id", selectedCategory);
      if (search) next = next.ilike("text", `%${search}%`);
      return next;
    },
  });

  // Reset page when filters change
  useEffect(() => {
    paginated.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrade, selectedSubject, selectedCategory, search]);

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error: optErr } = await supabase
        .from("options")
        .delete()
        .in("question_id", ids);
      if (optErr) throw optErr;
      const { error } = await supabase.from("questions").delete().in("id", ids);
      if (error) throw error;
    },
    onSuccess: (_d, ids) => {
      toast({
        title: ids.length > 1 ? "Questions deleted" : "Question deleted",
        description: `Successfully deleted ${ids.length} question${ids.length > 1 ? "s" : ""}.`,
      });
      setSelectedRows([]);
      setDeleteId(null);
      setBulkDeleteOpen(false);
      qc.invalidateQueries({ queryKey: queryKeys.questions.all });
    },
    onError: (err: any) =>
      toast({
        title: "Delete failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  const saveMutation = useMutation({
    mutationFn: async (updated: any) => {
      const { error: qErr } = await supabase
        .from("questions")
        .update({ text: updated.text, explanation: updated.explanation })
        .eq("id", updated.id);
      if (qErr) throw qErr;
      if (updated.deletedOptionIds?.length) {
        const { error } = await supabase
          .from("options")
          .delete()
          .in("id", updated.deletedOptionIds);
        if (error) throw error;
      }
      for (const opt of updated.options) {
        if (opt.id) {
          const { error } = await supabase
            .from("options")
            .update({ text: opt.text, is_correct: opt.is_correct })
            .eq("id", opt.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("options")
            .insert({ text: opt.text, is_correct: opt.is_correct, question_id: updated.id });
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      toast({ title: "Question updated" });
      setEditing(null);
      qc.invalidateQueries({ queryKey: queryKeys.questions.all });
    },
    onError: (err: any) =>
      toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const columns: ColumnDef<QuestionRow>[] = useMemo(
    () => [
      {
        id: "select",
        size: 40,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            aria-label="Select row"
          />
        ),
      },
      {
        header: "Question",
        accessorKey: "text",
        cell: ({ row }) => {
          const q = row.original;
          return (
            <div>
              <div className="font-medium">{q.text}</div>
              <ul className="text-sm text-muted-foreground mt-1 list-disc list-inside">
                {q.options?.map((o) => (
                  <li key={o.id} className={o.is_correct ? "font-medium text-green-600" : ""}>
                    {o.text} {o.is_correct && "(Correct)"}
                  </li>
                ))}
              </ul>
            </div>
          );
        },
      },
      {
        header: "Subject",
        size: 200,
        cell: ({ row }) => {
          const s = row.original.subject;
          return s ? `${s.name} (Grade ${s.grade})` : "—";
        },
      },
      {
        id: "actions",
        size: 120,
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(row.original)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDeleteId(row.original.id)}>
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  const handleExport = () => {
    const rows = paginated.rows.map((q) => ({
      id: q.id,
      text: q.text,
      subject: q.subject?.name ?? "",
      grade: q.subject?.grade ?? "",
      explanation: q.explanation ?? "",
      correct_options: q.options?.filter((o) => o.is_correct).map((o) => o.text).join(" | ") ?? "",
    }));
    exportRowsAsCsv(`questions-page-${paginated.page + 1}.csv`, rows);
  };

  const clearFilters = () => url.set({ grade: null, subject: null, category: null, q: null });

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex flex-col">
        <Header />
        <div className="flex-1 container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-6">Question Management</h1>
          <QuestionManagementMenu />

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                Filters
                <Button variant="outline" size="sm" onClick={clearFilters} className="gap-2">
                  <RefreshCw className="h-4 w-4" /> Clear Filters
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Grade</label>
                  <Select
                    value={selectedGrade}
                    onValueChange={(v) => url.set({ grade: v, subject: null, category: null })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {GRADE_OPTIONS.map((g) => (
                        <SelectItem key={g} value={g}>
                          Grade {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Subject</label>
                  <Select
                    value={selectedSubject}
                    onValueChange={(v) => url.set({ subject: v, category: null })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredSubjects.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} (Grade {s.grade})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Category</label>
                  <Select value={selectedCategory} onValueChange={(v) => url.set({ category: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCategories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <ListToolbar
            search={search}
            onSearchChange={(v) => url.set({ q: v || null })}
            searchPlaceholder="Search question text…"
            selectedCount={selectedRows.length}
            onClearSelection={() => setSelectedRows([])}
            bulkActions={
              <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
                Delete selected
              </Button>
            }
            onExportCsv={handleExport}
          />

          <div className="mt-3">
            <DataTable
              columns={columns}
              data={paginated.rows}
              isLoading={paginated.isLoading}
              isError={paginated.isError}
              onRetry={paginated.refetch}
              emptyTitle="No questions found"
              emptyDescription="Try adjusting your filters or search."
              enableRowSelection
              getRowId={(row) => row.id}
              onRowSelectionChange={setSelectedRows}
            />
            <DataPagination
              page={paginated.page}
              pageSize={paginated.pageSize}
              total={paginated.total}
              pageCount={paginated.pageCount}
              onPageChange={paginated.setPage}
              onPageSizeChange={paginated.setPageSize}
              isFetching={paginated.isFetching}
            />
          </div>
        </div>
        <Footer />
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Edit Question</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[calc(90vh-120px)] pr-4 -mr-4">
              <QuestionEditor
                question={editing as any}
                onSave={(u: any) => saveMutation.mutate(u)}
                onCancel={() => setEditing(null)}
              />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate([deleteId])}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {selectedRows.length} questions?</DialogTitle>
            <DialogDescription>
              This permanently deletes the selected questions and their options.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(selectedRows.map((r) => r.id))}
              disabled={deleteMutation.isPending}
            >
              Delete {selectedRows.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminQuestions;
